import {useToastContext} from '@github-ui/toast'
import {MilestoneIcon} from '@primer/octicons-react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  graphql,
  type PreloadedQuery,
  readInlineData,
  usePaginationFragment,
  usePreloadedQuery,
  useRelayEnvironment,
  useLazyLoadQuery,
} from 'react-relay'

import {ERRORS, HOTKEYS, LABELS, VALUES} from '../constants'
import {commitUpdateIssueMilestoneMutation} from '../mutations/update-issue-milestone-mutation'
import type {MilestoneListPaginationQuery} from './__generated__/MilestoneListPaginationQuery.graphql'
import type {
  MilestonePickerMilestone$data,
  MilestonePickerMilestone$key,
} from './__generated__/MilestonePickerMilestone.graphql'
import type {MilestonePickerPaginated$key} from './__generated__/MilestonePickerPaginated.graphql'
import type {MilestonePickerQuery} from './__generated__/MilestonePickerQuery.graphql'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from './ItemPicker'
import type {ItemGroup} from '../shared'
import {SharedEmptyPicker} from './SharedEmptyPicker'
import {SharedPicker} from './SharedPicker'
import {commitUpdateIssueMilestoneBulkByQueryMutation} from '../mutations/update-issue-milestone-bulk-by-query-mutation'
import {commitUpdateIssueMilestoneBulkMutation} from '../mutations/update-issue-milestone-bulk-mutation'
import type {updateIssueMilestoneBulkMutation$data} from '../mutations/__generated__/updateIssueMilestoneBulkMutation.graphql'
import type {updateIssueMilestoneBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueMilestoneBulkByQueryMutation.graphql'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'

export type Milestone = MilestonePickerMilestone$data

type MilestonePickerBaseProps = {
  issueId?: string
  activeMilestone: Milestone | null
  readonly: boolean
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  onIssueUpdate?: () => void
  /**
   * Whether to render the milestone picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
}

type MilestonePickerProps = MilestonePickerBaseProps & {
  milestoneQueryRef: PreloadedQuery<MilestonePickerQuery>
}

type LazyMilestonePickerProps = MilestonePickerBaseProps & {
  repo: string
  owner: string
}

type MilestonePickerInternalProps = MilestonePickerBaseProps & {
  milestoneQueryRef: MilestonePickerPaginated$key
  onSelectionChange: (milestone: Milestone[]) => void
}

const openGroup: ItemGroup = {groupId: 'open', header: {title: 'Open', variant: 'filled'}}
const closedGroup: ItemGroup = {groupId: 'closed', header: {title: 'Closed', variant: 'filled'}}

export const MilestonesPickerGraphqlQuery = graphql`
  query MilestonePickerQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...MilestonePickerPaginated
    }
  }
`

export const MilestoneFragment = graphql`
  fragment MilestonePickerMilestone on Milestone @inline {
    id
    title
    closed
    # eslint-disable-next-line relay/unused-fields
    dueOn
    # eslint-disable-next-line relay/unused-fields
    progressPercentage
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

export function MilestonePicker({
  issueId,
  milestoneQueryRef,
  readonly,
  activeMilestone,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  onIssueUpdate,
}: MilestonePickerProps) {
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedMilestones: Milestone[]) => {
      commitUpdateIssueMilestoneMutation({
        environment,
        input: {issueId: issueId ?? '', milestone: selectedMilestones.length > 0 ? selectedMilestones[0]! : null},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateMilestone,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [environment, issueId, onIssueUpdate, addToast],
  )

  return (
    <MilestonePickerBase
      issueId={issueId}
      milestoneQueryRef={milestoneQueryRef}
      readonly={readonly}
      activeMilestone={activeMilestone}
      onSelectionChange={onSelectionChange}
      insidePortal={insidePortal}
      shortcutEnabled={shortcutEnabled}
      anchorElement={anchorElement}
    />
  )
}

export function LazyMilestonePicker(props: LazyMilestonePickerProps) {
  const [fetchKey, setFetchKey] = useState(0)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit milestones right now',
    anchorElement: props.anchorElement,
  })

  return (
    <PreloadedQueryBoundary onRetry={() => setFetchKey(fetchKey + 1)} fallback={createFallbackComponent}>
      <LazyMilestonePickerInternal {...props} fetchKey={fetchKey} />
    </PreloadedQueryBoundary>
  )
}

function LazyMilestonePickerInternal({
  repo,
  owner,
  fetchKey,
  onIssueUpdate,
  issueId,
  ...rest
}: LazyMilestonePickerProps & {fetchKey: number}) {
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedMilestones: Milestone[]) => {
      commitUpdateIssueMilestoneMutation({
        environment,
        input: {issueId: issueId ?? '', milestone: selectedMilestones.length > 0 ? selectedMilestones[0]! : null},
        onError: () =>
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateMilestone,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [environment, issueId, onIssueUpdate, addToast],
  )
  const {repository} = useLazyLoadQuery<MilestonePickerQuery>(
    MilestonesPickerGraphqlQuery,
    {
      repo,
      owner,
    },
    {fetchKey},
  )
  return repository ? (
    <MilestonePickerInternalBase
      milestoneQueryRef={repository}
      issueId={issueId}
      {...rest}
      onSelectionChange={onSelectionChange}
    />
  ) : null
}

export function MilestonePickerBase({
  milestoneQueryRef,
  ...rest
}: MilestonePickerProps & {onSelectionChange: (milestone: Milestone[]) => void}) {
  const preloadedData = usePreloadedQuery<MilestonePickerQuery>(MilestonesPickerGraphqlQuery, milestoneQueryRef)
  return preloadedData.repository ? (
    <MilestonePickerInternalBase milestoneQueryRef={preloadedData.repository} {...rest} />
  ) : null
}

function MilestonePickerInternalBase({
  milestoneQueryRef,
  readonly,
  activeMilestone,
  onSelectionChange,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  nested,
  triggerOpen,
}: MilestonePickerInternalProps) {
  const [filter, setFilter] = useState('')

  const {data, loadNext, isLoadingNext, hasNext} = usePaginationFragment<
    MilestoneListPaginationQuery,
    MilestonePickerPaginated$key
  >(
    graphql`
      fragment MilestonePickerPaginated on Repository
      @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
      @refetchable(queryName: "MilestoneListPaginationQuery") {
        milestones(first: $count, after: $cursor) @connection(key: "Repository_milestones") {
          edges {
            node {
              ...MilestonePickerMilestone
            }
          }
          totalCount
        }
      }
    `,
    milestoneQueryRef,
  )

  const totalNumberOfMilestones = data.milestones?.totalCount || 0
  const fetchedMilestones = useMemo(
    () =>
      data.milestones?.edges?.flatMap(a =>
        a?.node ? [readInlineData<MilestonePickerMilestone$key>(MilestoneFragment, a.node)] : [],
      ) || [],
    [data],
  )

  useEffect(() => {
    if (hasNext && !isLoadingNext && fetchedMilestones.length < VALUES.milestonesMaxPreloadCount) {
      loadNext(VALUES.milestonesPageSize)
    }
  }, [loadNext, fetchedMilestones, totalNumberOfMilestones, hasNext, isLoadingNext])

  const items = useMemo(() => {
    const repositoryMilestones = fetchedMilestones.slice().sort((a, b) => a.title.localeCompare(b.title))
    if (!filter) return repositoryMilestones
    return repositoryMilestones.filter(l => l.title.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedMilestones, filter])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((milestone: Milestone) => milestone.id, [])

  const convertToItemProps = useCallback((milestone: Milestone): ExtendedItemProps<Milestone> => {
    return {
      id: milestone.id,
      text: milestone.title,
      source: milestone,
      groupId: milestone.closed ? closedGroup.groupId : openGroup.groupId,
      leadingVisual: () => <MilestoneIcon size={16} />,
    }
  }, [])

  // Only show groups for which the filter returns at least one item
  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (items.some(i => !i.closed)) {
      itemGroups.push(openGroup)
    }
    if (items.some(i => i.closed)) {
      itemGroups.push(closedGroup)
    }
    return itemGroups
  }, [items])

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      if (activeMilestone) {
        return (
          <SharedPicker
            label={LABELS.noMilestones}
            sharedPickerMainValue={activeMilestone?.title}
            anchorProps={readonly ? undefined : anchorProps}
            ariaLabel={LABELS.selectMilestones}
            readonly={readonly}
            nested={nested}
          />
        )
      } else {
        return (
          <SharedEmptyPicker
            leadingIcon={MilestoneIcon}
            hotKey={!readonly && shortcutEnabled ? HOTKEYS.milestonePicker.toUpperCase() : undefined}
            label={LABELS.noMilestones}
            anchorProps={readonly ? undefined : anchorProps}
            readonly={readonly}
            nested={nested}
          />
        )
      }
    },
    [activeMilestone, anchorElement, nested, readonly, shortcutEnabled],
  )

  return (
    <ItemPicker
      items={items}
      initialSelectedItems={activeMilestone ? [activeMilestone] : []}
      openHotkey={shortcutEnabled ? HOTKEYS.milestonePicker : undefined}
      filterItems={filterItems}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter milestones ${
        fetchedMilestones.length >= VALUES.milestonesMaxPreloadCount
          ? `(${VALUES.milestonesMaxPreloadCount} milestones loaded)`
          : ''
      }`}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      groups={groups}
      renderAnchor={renderAnchor}
      insidePortal={insidePortal}
      height={'large'}
      width={'medium'}
      nested={nested}
      resultListAriaLabel={'Milestone results'}
      triggerOpen={triggerOpen}
    />
  )
}

type BulkIssueMilestonePickerProps = Omit<MilestonePickerProps, 'issueId'> & SharedBulkActionsItemPickerProps

export function BulkIssueMilestonePicker({
  onError,
  onCompleted,
  issuesToActOn,
  useQueryForAction,
  repositoryId,
  query,
  ...rest
}: BulkIssueMilestonePickerProps) {
  const environment = useRelayEnvironment()
  const onSelectionChange = useCallback(
    (selectedMilestone: Milestone[]) => {
      const milestoneId = selectedMilestone.length === 1 ? selectedMilestone[0]?.id : null
      if (!milestoneId) return
      const mutationArgs = {
        environment,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueMilestoneBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            milestoneId,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueMilestoneBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueMilestoneBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            milestoneId,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueMilestoneBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [environment, issuesToActOn, onCompleted, onError, query, repositoryId, useQueryForAction],
  )
  return <MilestonePickerBase onSelectionChange={onSelectionChange} {...rest} />
}

try{ MilestonePicker.displayName ||= 'MilestonePicker' } catch {}
try{ LazyMilestonePicker.displayName ||= 'LazyMilestonePicker' } catch {}
try{ LazyMilestonePickerInternal.displayName ||= 'LazyMilestonePickerInternal' } catch {}
try{ MilestonePickerBase.displayName ||= 'MilestonePickerBase' } catch {}
try{ MilestonePickerInternalBase.displayName ||= 'MilestonePickerInternalBase' } catch {}
try{ BulkIssueMilestonePicker.displayName ||= 'BulkIssueMilestonePicker' } catch {}